import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, LOCALES, LOCALE_DIR, isLocale, resources, type Locale } from '@worki/shared';
import { api } from './lib/api.js';
import { actionAllowanceResources } from './lib/actionAllowanceCopy.js';

const STORAGE_KEY = 'worki.locale';

/**
 * Hebrew is the default and the fallback, not English: this is a Hebrew-native
 * product, so an untranslated key should surface in Hebrew rather than break
 * into a second language mid-screen.
 *
 * Which is why the browser's own language list is not consulted. It used to be,
 * and because `en` is a language this app speaks, a browser reporting `en-US` —
 * the setting most machines ship with, whoever is sitting at them — was enough
 * to open the product in English at a business whose every customer reads
 * Hebrew. A language the visitor never asked for is a worse guess than the one
 * the product is written in. Choosing from the switcher is the way in, and that
 * choice is what is remembered here.
 */
function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

/** Keeps `<html lang>`/`<html dir>` in step so CSS logical properties flip. */
export function applyDocumentLocale(locale: Locale): void {
  document.documentElement.lang = locale;
  document.documentElement.dir = LOCALE_DIR[locale];
}

const locale = initialLocale();

void i18next.use(initReactI18next).init({
  resources: actionAllowanceResources(resources),
  lng: locale,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...LOCALES],
  defaultNS: 'translation',
  interpolation: {
    // React already escapes rendered values.
    escapeValue: false,
  },
  returnNull: false,
});

applyDocumentLocale(locale);

export async function changeLocale(next: Locale): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Storage blocked; the choice still applies for this session.
  }
  await applyLanguage(next);
}

async function applyLanguage(next: Locale): Promise<void> {
  await i18next.changeLanguage(next);
  applyDocumentLocale(next);

  // Mirror the choice to the API so emails and PDFs render in the same language.
  // Accept-Language rides every authed fetch (native PDFs cannot rely on the locale cookie).
  await api.post('/auth/locale', { locale: next }).catch(() => undefined);
}

export function currentLocale(): Locale {
  return isLocale(i18next.language) ? i18next.language : DEFAULT_LOCALE;
}

export default i18next;
